





































import {
  computed, defineComponent, reactive, ref,
} from '@vue/composition-api';
import { useQuery } from '@vue/apollo-composable';
import { useFormat } from '@ideavivat/vivat-2';

import CommonHubSelector from '@/components/CommonHubSelector.vue';
import ChartWithMoneyAxis from '@/components/Vivat/Chart/ChartWithMoneyAxis.vue';

import {
  NOW_START_MONTH,
  NOW_END_MONTH,
  monthDays,
} from '@/utils';
import useAuth from '@/useAuth';

const ChartWithSelectedMonth = defineComponent({
  components: {
    CommonHubSelector,
    ChartWithMoneyAxis,
  },
  props: {
    primaryColor: {
      type: String,
      default: 'rgba(18,74,136,255)',
    },
    secondaryColor: {
      type: String,
      default: 'rgba(80,161,242,255)',
    },
    query: {
      default: undefined,
    },
    result: {
      type: Function,
      default: undefined,
    },
    title: {
      type: String,
      default: '',
    },
    formatYAxis: {
      type: Boolean,
      default: true,
    },
    height: {
      type: String,
      default: '180',
    },
    showPercentageLine: { type: Boolean, default: false },
    yTicks: { type: Object, default: undefined },
  },
  setup(props) {
    const { me } = useAuth();
    const filter = reactive({
      hub: null,
      monthRange: {
        from: NOW_START_MONTH,
        to: NOW_END_MONTH,
      },
    });
    const max = ref(0);
    const min = ref(0);
    const endOfMonth = computed(() => {
      const curMonth = filter.monthRange.to;
      return useFormat.date(curMonth).computerShortDateEndOfMonth() - 1;
    });

    const { result, loading } = useQuery(
      props.query as any,
      computed(() => {
        const vars = {
          timeframe: filter.monthRange,
          hubId: undefined,
        };

        if (filter.hub && me.hub.hubType === 'MAIN') {
          vars.hubId = (filter.hub as any)?.id;
        } else if (me.hub.hubType !== 'MAIN') {
          vars.hubId = me.hub.id as any;
        }

        return vars;
      }),
      () => ({
        fetchPolicy: 'network-only',
        enabled: props.query !== undefined && props.result !== undefined,
      }),
    );

    const rawData = computed(() => {
      if (loading.value === false && result.value && props.result !== undefined) {
        return {
          expenseRange: props.result(result.value),
        };
      }
      return { expenseRange: [] };
    });
    const mapRawDataIntoFormat = computed(() => {
      const primary: any = [];
      const secondary: any = [];
      max.value = 0;
      for (let i = 0; i < rawData.value.expenseRange.length; i += 1) {
        primary.push(rawData.value.expenseRange[i].fullTruckLoaded);
        secondary.push(rawData.value.expenseRange[i].lessThanTruckLoaded);
        const sum = rawData.value.expenseRange[i].fullTruckLoaded + rawData.value.expenseRange[i].lessThanTruckLoaded;
        if (sum > max.value) max.value = sum;
        if (sum < min.value) min.value = sum;
      }
      const ceilMax = Math.ceil(max.value);
      const firstDigit = ceilMax.toString()[0];
      const ceilMaxLength = ceilMax.toString().length;
      const addedMax = parseInt(firstDigit, 10) * (10 ** (ceilMaxLength - 1));
      max.value = addedMax + (addedMax / 1);
      return {
        primary,
        secondary,
      };
    });

    const chartData = computed(() => ({
      labels: monthDays(filter.monthRange.from, filter.monthRange.to),
      datasets: [
        {
          label: 'FTL',
          data: mapRawDataIntoFormat.value.primary,
          backgroundColor: props.primaryColor,
          stack: 'CURRENT',
        },
        {
          label: 'LTL',
          data: mapRawDataIntoFormat.value.secondary,
          backgroundColor: props.secondaryColor,
          stack: 'CURRENT',
        },
      ],
    }));

    return {
      endOfMonth,
      me,
      filter,
      chartData,
      rawData,
      max,
      min,
    };
  },
});

export default ChartWithSelectedMonth;
